import React, { useCallback, useEffect, useRef, useState } from "react";
import { Typography, Select, Modal } from "antd";
import ReusableButton from "../FormFields/ReusableButton";
import CommonAttachment from "../Common/CommonAttachment";
import ReactQuill from "react-quill";
import "quill/dist/quill.snow.css";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import DOMPurify from "dompurify";
import { removeBase64Images } from "../../utils/constants";
import { useNotification } from "../../contexts/NotificationProvider";
import { useAxios } from "../../hooks/useAxios";
import { useNavigate } from "react-router-dom";

const SendNewsletter = () => {
  const navigate = useNavigate();
  const notify = useNotification();
  const { axiosInstance } = useAxios();
  const quillRef = useRef(null);

  const [selectedUsers, setSelectedUsers] = useState([]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [editorValue, setEditorValue] = useState("");
  const [buttonLoading, setButtonLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [employees, setEmployees] = useState({ results: [] });
  const [searchText, setSearchText] = useState("");
  const debounceTimeout = useRef(null);
  const [loading, setLoading] = useState(false);
  const allUserIds = employees.results.map((emp) => emp.id);

  const allSelected = selectedUsers.length === allUserIds.length;

  const fetchEmployees = async (searchTerm = "") => {
    const params = searchTerm ? { search: searchTerm } : {};
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `/api/employees/?page_size=5000`,
        {
          params,
        }
      );
      setEmployees(response?.data);
    } catch (e) {
      console.error("Error fetching employees", e);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = useCallback((value) => {
    setSearchText(value);

    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);

    debounceTimeout.current = setTimeout(() => {
      fetchEmployees(value);
    }, 400);
  }, []);

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill || !quill.root) return;

    const handlePaste = (e) => {
      const clipboardData = e.clipboardData;
      if (!clipboardData) return;

      const hasImage = [...clipboardData.items].some((item) =>
        item.type.startsWith("image/")
      );

      if (
        hasImage ||
        clipboardData.getData("text/html")?.includes("data:image/")
      ) {
        e.preventDefault();
        notify({ type: "error", message: "Images are not allowed." });
      }
    };

    quill.root.addEventListener("paste", handlePaste);
    return () => quill.root.removeEventListener("paste", handlePaste);
  }, []);

  const handleUserChange = (value) => {
    if (value.includes("all")) {
      setSelectedUsers(allSelected ? [] : allUserIds);
    } else {
      setSelectedUsers(value);
    }
  };

  const handleSendNewsletter = async () => {
    if (!selectedUsers.length || !editorValue || !uploadedFile) {
      notify({ type: "error", message: "All fields are required." });
      return;
    }

    if (uploadedFile.size / 1024 / 1024 > 8) {
      notify({ type: "error", message: "File size should be less than 8MB." });
      return;
    }

    const formData = new FormData();
    selectedUsers.forEach((id) => formData.append("users", id));
    const sanitizedHTML = DOMPurify.sanitize(removeBase64Images(editorValue));
    formData.append("content", sanitizedHTML);
    formData.append("file", uploadedFile);

    try {
      setButtonLoading(true);
      await axiosInstance.post("/api/newsletters/", formData);
      setIsModalOpen(true);
    } catch (error) {
      notify({
        type: "error",
        message: "Failed to send newsletter.",
        description: error?.response?.data?.detail || error.message,
      });
    } finally {
      setButtonLoading(false);
    }
  };

  return (
    <>
      <Modal width={300} open={isModalOpen} footer={null} closable={false}>
        <div className="success-checkmark">
          <div className="check-icon">
            <span className="icon-line line-tip"></span>
            <span className="icon-line line-long"></span>
            <div className="icon-circle"></div>
            <div className="icon-fix"></div>
          </div>
        </div>
        <Typography.Title style={{ textAlign: "center" }} level={4}>
          Newsletter sent successfully!
        </Typography.Title>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <ReusableButton
            text="Done"
            onClick={() => navigate("/newsletters")}
          />
        </div>
      </Modal>

      <div style={{ padding: "18px", background: "#fff", borderRadius: "6px" }}>
        <Typography.Title level={4}>Users</Typography.Title>
        <Select
          size="large"
          loading={loading}
          maxTagCount="responsive"
          mode="multiple"
          style={{ width: "100%", maxWidth: "484px", height: "60px" }}
          placeholder="Select employees"
          value={selectedUsers}
          onSearch={handleSearch}
          searchValue={searchText}
          filterOption={false}
          onChange={handleUserChange}
          onDropdownVisibleChange={(open) => {
            setOpen(open);
            if (!open) {
              setSearchText("");
              fetchEmployees(""); // fetch full list when dropdown closes
            }
          }}
          suffixIcon={open ? <UpOutlined /> : <DownOutlined />}
          options={[
            {
              label: allSelected ? "Unselect All" : "Select All",
              value: "all",
            },
            ...employees.results.map((emp) => ({
              label: `${emp.first_name} ${emp.last_name}`,
              value: emp.id,
            })),
          ]}
        />

        <Typography.Title level={4} style={{ margin: "8px 0" }}>
          Message
        </Typography.Title>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={editorValue}
          onChange={setEditorValue}
          style={{ height: "180px" }}
          placeholder="Write your message..."
          modules={{
            toolbar: [
              ["bold", "italic", "underline"],
              [{ list: "ordered" }, { list: "bullet" }],
              ["link"],
            ],
          }}
        />

        <CommonAttachment
          file={uploadedFile}
          onUpload={setUploadedFile}
          onRemove={() => setUploadedFile(null)}
        />

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <ReusableButton
            text="Send"
            onClick={handleSendNewsletter}
            loading={buttonLoading}
            disabled={!selectedUsers.length || !uploadedFile || !editorValue}
            customStyles={{ marginTop: "20px" }}
          />
        </div>
      </div>
    </>
  );
};

export default React.memo(SendNewsletter);
